import { DateTime } from 'luxon';
import { useTransactions } from './api/use-get-transactions';
import { useTimeZone } from '../user/api/use-get-user';
import { useMemo } from 'react';
import { useFutureRecurringTransactions } from '../recurring-transaction/recurring-transaction-utils';
/**
 * Sorts transactions by date in descending order (latest first)
 * and uses ID as a secondary sort criterion in descending order
 *
 * @param transactions - Array of transactions to sort
 * @returns Sorted array of transactions
 */
export function sortTransactionsDescending(transactions) {
    return [...transactions].sort((a, b) => {
        const dateComparison = b.dateEpoch - a.dateEpoch;
        return dateComparison !== 0 ? dateComparison : b.id.localeCompare(a.id);
    });
}
/**
 * Gets future transactions from a list of transactions sorted in descending order by date
 *
 * This function efficiently finds future transactions by breaking early once
 * a past transaction is encountered, taking advantage of the descending date order.
 *
 * @param transactions - Array of transactions sorted in descending order by date
 * @param timezone - Timezone to use for date comparison
 * @return Array of future transactions (maintains original descending order)
 */
export function getFutureTransactions(transactions, timezone) {
    if (!transactions.length) {
        return [];
    }
    const now = DateTime.now().setZone(timezone).startOf('day');
    const futureTransactions = [];
    // Loop through transactions (already sorted in descending order)
    for (const transaction of transactions) {
        const transactionDate = DateTime.fromSeconds(transaction.dateEpoch).setZone(timezone).startOf('day');
        // If transaction is in the future, add it to our results
        if (transactionDate > now) {
            futureTransactions.push(transaction);
        }
        else {
            // Since transactions are sorted by date descending,
            // once we find a non-future transaction, we can stop looking
            break;
        }
    }
    return futureTransactions;
}
/**
 * Hook that provides future transactions from a specified year/month
 *
 * @param year - Year to filter transactions for
 * @param month - Month to filter transactions for (1-12)
 * @returns Array of future transactions sorted by date (latest first) and id (newest first)
 */
export function useFutureTransactions(year, month) {
    const transactions = useTransactions();
    const timezone = useTimeZone();
    // Filter and sort for future transactions
    return useMemo(() => {
        // If we're looking at a past month, there are no future transactions
        const now = DateTime.now().setZone(timezone);
        const viewMonth = DateTime.fromObject({ year, month }, { zone: timezone });
        // If the viewed month is in the past, return empty array
        if (viewMonth.startOf('month') < now.startOf('month')) {
            return [];
        }
        return getFutureTransactions(transactions, timezone);
    }, [transactions, year, month, timezone]);
}
/**
 * Hook that provides both future regular transactions and future recurring transactions
 * for the specified month, combined into a single sorted list
 *
 * @param year - Year to calculate transactions for
 * @param month - Month to calculate transactions for (1-12)
 * @returns Array of all future transactions sorted by date (latest first) and id (newest first)
 */
export function useAllFutureTransactions(year, month) {
    const futureRegularTransactions = useFutureTransactions(year, month);
    const futureRecurringTransactions = useFutureRecurringTransactions(year, month);
    // Combine and sort all future transactions
    return useMemo(() => {
        const allFutureTransactions = [...futureRegularTransactions, ...futureRecurringTransactions];
        return sortTransactionsDescending(allFutureTransactions);
    }, [futureRegularTransactions, futureRecurringTransactions]);
}
/**
 * Hook that provides access to all unique payees from transactions.
 *
 * This hook returns a sorted array of all unique payee names that have been
 * used in transactions.
 *
 * @returns An array of unique, sorted payee strings.
 */
export function usePayees() {
    const transactions = useTransactions();
    return useMemo(() => extractUniquePayees(transactions), [transactions]);
}
/**
 * Hook that provides a function to filter payee suggestions based on user input.
 *
 * This hook delegates to the pure `extractUniquePayees` function.
 *
 * @returns A function that takes an input string and returns up to 5 matching payees.
 */
/**
 * Hook that provides a function to filter payee suggestions based on user input.
 *
 * This hook uses the usePayees hook to get all payees and then provides a function
 * to filter them based on user input.
 *
 * @returns A function that takes an input string and returns up to the specified limit of matching payees.
 */
export function usePayeeSuggestions() {
    const payees = usePayees();
    return useMemo(() => {
        return (inputText, limit = 5) => {
            if (!inputText)
                return payees.slice(0, limit);
            const normalizedInput = inputText.toLowerCase();
            const suggestions = [];
            for (const payee of payees) {
                if (payee.toLowerCase().includes(normalizedInput)) {
                    suggestions.push(payee);
                    if (suggestions.length === limit) {
                        break;
                    }
                }
            }
            return suggestions;
        };
    }, [payees]);
}
/**
 * Extracts a distinct, trimmed, and sorted list of payees from a list of transactions.
 *
 * @param transactions - Array of Transaction objects.
 * @returns A sorted array of unique payee strings.
 */
export function extractUniquePayees(transactions) {
    const payeeMap = new Map();
    transactions.forEach((tx) => {
        if (!tx.payee) {
            return;
        }
        const trimmed = tx.payee.trim();
        if (!trimmed) {
            return;
        }
        const normalized = trimmed.toLowerCase();
        if (!payeeMap.has(normalized)) {
            payeeMap.set(normalized, trimmed);
        }
    });
    // Sort payees using case-insensitive comparison.
    return Array.from(payeeMap.values()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}
